import withRouter from "hoc/withRouter";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  TextField,
} from "@mui/material";
import { useGetPlanUsage } from "hooks/useGetPlanUsage";
import { useGetPlans } from "hooks/useGetPlans";
import { Plan } from "interfaces/entities/plan.interface";
import PlanCard from "./PlanCard";
import PageTitle from "components/PageTitle/PageTitle";
import { useEffect, useState } from "react";
import subscriptionService from "services/admin/subscription.service";
import { toast } from "react-toastify";
import { useGetSubscription } from "hooks/useGetSubscription";
import { toCurrencyFormat } from "helpers/utils";
import { queryClient } from "queryClient";

const PlansList = () => {
  const { data, isLoading, error } = useGetPlans(localStorage.getItem("orgID") || "");
  const { data: existingSubscription } = useGetSubscription(localStorage.getItem("orgID") || "");
  const { data: usage } = useGetPlanUsage(localStorage.getItem("orgID") || "");

  const [planToSubscribe, setPlanToSubscribe] = useState<Plan | null>(null);
  const [isAgreeWithPaymentPolicy, setIsAgreeWithPaymentPolicy] = useState(false);
  const [isOpenConfirmSubscription, setIsOpenConfirmSubscription] = useState(false);
  const [isOpenConfirmUnsubscribe, setIsOpenConfirmUnsubscribe] = useState(false);
  // Срок действия карты (ММ/ГГ). Банк не даёт автосписания позже срока карты, а сам его не сообщает.
  const [cardExp, setCardExp] = useState("");
  const cardExpError = getCardExpError(cardExp);
  const isPastDue = existingSubscription?.status === "PAST_DUE";
  // Демо-доступ: идёт пробный период или он уже закончился (axios перенаправляет сюда с ?trial=expired)
  const isTrialing = existingSubscription?.status === "TRIALING";
  const isTrialExpired = !existingSubscription && new URLSearchParams(window.location.search).get("trial") === "expired";

  // Возврат со страницы банка: /admin/plans?payment=success|failed
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (!payment) return;
    if (payment === "success") {
      toast.success("Оплата прошла успешно! Подписка оформлена.");
      // Уведомление банка может прийти чуть позже редиректа — обновим статус ещё раз через пару секунд
      const orgID = localStorage.getItem("orgID") || "";
      queryClient.invalidateQueries({ queryKey: ["subscription", orgID] });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["subscription", orgID] }), 3000);
    } else {
      toast.error("Оплата не прошла. Попробуйте ещё раз или используйте другую карту.");
    }
    params.delete("payment");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, []);

  const subscribe = async () => {
    try {
      if (existingSubscription && !isTrialing && existingSubscription?.planID !== planToSubscribe?.id) {
        // Смена тарифа
        if (!window.confirm("Вы уверены, что хотите сменить тариф?")) {
          return;
        }
      }

      const windowWidth = window.outerWidth;
      const windowHeight = window.outerHeight;
      const { data: res } = await subscriptionService.post({
        planID: planToSubscribe?.id,
        cardExp,
        window: {
          width: windowWidth,
          height: windowHeight,
        },
      });

      if (!res) {
        toast.error("Ошибка при подписке на тариф!");
        return;
      }

      if (res.url) {
        window.location.href = `${process.env.REACT_APP_API_ADDRESS}${res.url}`;
      }
    } catch (error: any) {
      console.log(error);
      toast.error(error?.response?.data?.error || "Ошибка при подписке на тариф!");
      setPlanToSubscribe(null);
    } finally {
      setIsOpenConfirmSubscription(false);
    }
  };

  const unsubscribe = async () => {
    try {
      await subscriptionService.patch(existingSubscription?.id, {});
      toast.success(`Подписка отменена успешно!`);
      setPlanToSubscribe(null);
      queryClient.invalidateQueries({
        queryKey: ["subscription", localStorage.getItem("orgID") || ""],
      });
    } catch (error) {
      console.log(error);
      toast.error("Ошибка при отписке от тарифа!");
    } finally {
      setIsOpenConfirmUnsubscribe(false);
    }
  };

  const showConfirmSubscription = (plan: Plan) => {
    setPlanToSubscribe(plan);
    setCardExp("");
    setIsOpenConfirmSubscription(true);
  };

  const showConfirmUnsubscribe = () => {
    setIsOpenConfirmUnsubscribe(true);
  };

  function buildContent() {
    if (error) {
      return <div>{error.message}</div>;
    }
    if (isLoading) {
      return (
        <div className="text-center">
          <CircularProgress></CircularProgress>
        </div>
      );
    }
    if (data?.length === 0) {
      return (
        <div>
          Тарифы отсутсвуют. Чтобы добавить тариф пожалуйста, нажмите на кнопку "Добавить" сверху
        </div>
      );
    }
    if (data?.length && data?.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.map((plan: Plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              subscribe={() => showConfirmSubscription(plan)}
              unsubscribe={() => showConfirmUnsubscribe()}
              isSubscribed={existingSubscription && existingSubscription.planID === plan.id}
              updateCard={isPastDue ? () => showConfirmSubscription(plan) : undefined}
            />
          ))}
        </div>
      );
    }
  }
  return (
    <div>
      <PageTitle>Список тарифов</PageTitle>
      {isTrialing && (
        <Alert severity="info" className="mb-4">
          Сейчас у вас пробный период до {new Date(existingSubscription.currentPeriodEnd).toLocaleString("ru-RU")}.
          Выберите тариф, чтобы продолжить работу после его окончания — все ваши данные сохранятся.
        </Alert>
      )}
      {isTrialExpired && (
        <Alert severity="warning" className="mb-4">
          Пробный период закончился. Выберите тариф, чтобы продолжить работу — все ваши данные сохранены.
        </Alert>
      )}
      {isPastDue && (
        <Alert severity="warning" className="mb-4">
          Не удалось списать оплату за подписку. Доступ сохранится ещё несколько дней — пополните
          карту или привяжите новую, нажав «Обновить карту» на текущем тарифе.
          {existingSubscription?.lastPaymentError && (
            <div className="text-xs mt-1">Ответ банка: {existingSubscription.lastPaymentError}</div>
          )}
        </Alert>
      )}
      {usage && usage.length > 0 && (
        <div className="mb-6 p-4 bg-white rounded border">
          <div className="font-bold mb-3">Использование тарифа</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {usage.map((u) => {
              const reached = u.limit !== null && u.used >= u.limit;
              return (
                <div key={u.code}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{u.name}{u.code === "ai_requests_per_month" ? " (этот месяц)" : ""}</span>
                    <span className={reached ? "text-red-600 font-bold" : ""}>
                      {u.used.toLocaleString("ru-RU")} из {u.limit === null ? "∞" : u.limit.toLocaleString("ru-RU")}
                    </span>
                  </div>
                  {u.limit !== null && (
                    <LinearProgress
                      variant="determinate"
                      color={reached ? "error" : "primary"}
                      value={Math.min(100, u.limit === 0 ? 100 : (u.used / u.limit) * 100)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {buildContent()}
      {/* Diaglog подтверждения подписки */}
      <Dialog open={isOpenConfirmSubscription} onClose={() => setIsOpenConfirmSubscription(false)}>
        <DialogContent>
          <DialogTitle>Подтверждение подписки</DialogTitle>
          <DialogContentText>
            <div>
              Я согласен на оформление подписки с оплатой{" "}
              <strong>
                {toCurrencyFormat(planToSubscribe?.price || 0, planToSubscribe?.currency || "KZT")}
              </strong>{" "}
              / месяц. Подписка продлевается автоматически. Условие оплаты:{" "}
              <a href="https://ubee.kz/payment-policy" target="_blank">
                Условие оплаты
              </a>{" "}
              также{" "}
              <a href="https://ubee.kz/refund-policy" target="_blank">
                Политика возврата и отмены подписки
              </a>
            </div>
            <div className="mt-4">
              <Checkbox
                checked={isAgreeWithPaymentPolicy}
                onChange={(e) => setIsAgreeWithPaymentPolicy(e.target.checked)}
              />
              Я согласен с условиями оплаты и политикой возврата и отмены подписки
            </div>
            <div className="mt-4">
              <TextField
                label="Срок действия карты"
                placeholder="ММ/ГГ"
                value={cardExp}
                onChange={(e) => setCardExp(formatCardExp(e.target.value))}
                error={cardExp.length > 0 && !!cardExpError}
                helperText={
                  (cardExp.length > 0 && cardExpError) ||
                  "Как на лицевой стороне карты, которой будете оплачивать. Номер карты вводится на странице банка."
                }
                inputProps={{ inputMode: "numeric", maxLength: 5 }}
                size="small"
                fullWidth
              />
            </div>
          </DialogContentText>
          <DialogActions>
            <Button
              onClick={() => {
                setIsOpenConfirmSubscription(false);
                setPlanToSubscribe(null);
              }}
            >
              Отменить
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={subscribe}
              disabled={!isAgreeWithPaymentPolicy || !!cardExpError}
            >
              Оформить подписку
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Dialog подтверждения отмены подписки */}
      <Dialog open={isOpenConfirmUnsubscribe} onClose={() => setIsOpenConfirmUnsubscribe(false)}>
        <DialogContent>
          <DialogTitle>Подтверждение отмены подписки</DialogTitle>
          <DialogContentText>
            <div>
              Вы уверены, что хотите отменить подписку? После отмены доступ к функциям будет
              доступен до конца оплаченного периода. Все ваши данные будут сохранены.
            </div>
          </DialogContentText>
          <DialogActions>
            <Button onClick={() => setIsOpenConfirmUnsubscribe(false)}>Отменить</Button>
            <Button variant="contained" color="primary" onClick={() => unsubscribe()}>
              Отменить подписку
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// "1033" → "10/33"
function formatCardExp(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

function getCardExpError(value: string): string | null {
  const match = value.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return "Введите срок в формате ММ/ГГ";
  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  if (month < 1 || month > 12) return "Неверный месяц";
  const now = new Date();
  if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
    return "Срок действия карты истёк";
  }
  return null;
}

export default withRouter(PlansList);
